import { Observer } from 'mobx-react';
import { AvatarModalComponent, useLoadPreviewAvatar } from 'modules/Shared/AvatarAccountUpload';
import { FC, useEffect, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';

import { ODIN_URLS, RESOLUTIONS } from 'src/constants';

import { appEnvControl, generateGenderOptions, t } from 'utils';

import { countriesStore, form, progressStore } from 'stores';

import { ACCOUNT_INFO_FORM_ID, AVATAR_KEY } from '../../constants';

import { mountAccountInfo, unmountAccountInfo, updateAccountField, updateAccountInfo } from '../../actions';

import { useDebounceLiveUpdate } from '../../hooks';

type Props = {};

const FormWrapper = styled.div`
  width: 100%;
  margin-top: 32px;
  form > div:not(:last-child) {
    margin-bottom: 32px;
  }
  form span[disabled] {
    color: inherit;
  }
`;

const AvatarUploadWrapper = styled.div`
  margin-top: 48px;
  @media (min-width: ${RESOLUTIONS.small}px) {
    margin-left: 30px;
  }
`;

const AccountMessageBanner = styled.div`
  margin-top: 32px;
  padding: 16px 20px;
  background-color: #e7f3ff;
  border-left: 4px solid #0066cc;
  border-radius: 4px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
  }

  a {
    color: #0066cc;
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AccountInformationForm: FC<Props> = () => {
  const debounceLiveUpdate = useDebounceLiveUpdate(updateAccountField);
  const liveUpdateBirth = useDebounceLiveUpdate(updateAccountField, 2000);
  const { isShowAvatarModal, imagePreviewUrl, closeModal, handleChangeFile } = useLoadPreviewAvatar();
  const didChangeRef = useRef<boolean>(false);

  useEffect(() => {
    mountAccountInfo();
    return () => {
      unmountAccountInfo();
    };
  }, []);

  const changePreviewUrl = (image: string) => {
    form.onChange(ACCOUNT_INFO_FORM_ID, AVATAR_KEY, image);
    handleLiveUpdate({ name: [AVATAR_KEY] });
  };

  const handleLiveUpdate = (value: AnyObject) => debounceLiveUpdate(value);

  const handleOnBlur = ({ target }) => {
    const changes = { name: [target?.name], value: target?.value };

    handleLiveUpdate(changes);
  };

  return (
    <Observer>
      {() => {
        const avatar = form.fetch<string>(ACCOUNT_INFO_FORM_ID, 'avatar');
        const allCountries = countriesStore.valuesForDropdown();
        const allNationalities = countriesStore.valuesForNationalityDropdown();
        const genderOptions = generateGenderOptions();
        const isLoadingProcess = progressStore.isLoading(ACCOUNT_INFO_FORM_ID);

        const accountManagementUrl = `${ODIN_URLS[appEnvControl.currentEnvWithDefaultDev()]}/account?redirectUri=${encodeURIComponent(
          location.origin + location.pathname + location.search,
        )}`;

        return (
          <TestAnchorContext.Provider value={{ container: 'Profile.Account-Information' }}>
            <AvatarModalComponent
              open={isShowAvatarModal}
              src={imagePreviewUrl}
              changePreviewUrl={changePreviewUrl}
              closeModal={closeModal}
            />
            <AccountMessageBanner>
              <p>
                {t.staticAsString('settings.newAuthAccountMessage')}{' '}
                <a href={accountManagementUrl} target='_self' rel='noopener noreferrer'>
                  {t.staticAsString('settings.manageYourAccount')}
                </a>
              </p>
            </AccountMessageBanner>
          </TestAnchorContext.Provider>
        );
      }}
    </Observer>
  );
};
